import { OffsetTime } from 'components/timePicker';

import { FormType } from 'pages/merchant/edit/sections/kyb/keys';
import {
  AssociatedPersonType,
  BridgeAccountPurpose,
  BridgeAssociatedPerson,
  BridgeCustomerAddress,
  BridgeCustomerBusinessType,
  BridgeDocument,
  BridgeHighRiskActivities,
  BridgeIdentityInformation,
  BridgeRegulatedActivity,
  BridgeSourceOfFunds,
} from 'pages/merchant/edit/sections/kyb/types';

import { CryptoPayout } from 'types/cryptoPayout';
import { CurrencyCode } from 'types/currency';
import { CustodialWalletTransaction } from 'types/custodialWallet';
import { ValidationAPIError } from 'types/error';
import { DateFilterValue } from 'types/filters';
import { Response } from 'types/http';
import { RequestPagination, ServerPagination } from 'types/pagination';
import { LiquidityProvider } from 'types/pwcOrder';
import { RequestSorting } from 'types/sort';
import { DayOfMonth, FiatPayoutFrequency, Weekday } from 'types/time';

export interface MerchantsListQuery {
  filters?: {
    uuids?: string[];
    date?: DateFilterValue;
    status?: string;
    pwcEnabled?: boolean;
    cryptoPayoutEnabled?: boolean;
    accountUuids?: string[];
  };
  search?: string;
  pagination?: RequestPagination;
  sort?: RequestSorting;
}

export enum MerchantStatus {
  Active = 'active',
  Onboarding = 'onboarding',
  Deactivated = 'deactivated',
  Inactive = 'inactive',
}

export enum AccountOwnerType {
  Individual = 'individual',
  Business = 'business',
}

export enum BridgeFiatRail {
  SEPA = 'sepa',
  ACH = 'ach',
  ACH_SAME_DATE = 'ach_same_day',
  WIRE = 'wire',
  SWIFT = 'swift',
  SPEI = 'spei',
  PIX = 'pix',
  BRE_B = 'bre_b',
}

export enum ExternalAccountType {
  US = 'us',
  IBAN = 'iban',
  UNKNOWN = 'unknown',
  CLABE = 'clabe',
  PIX = 'pix',
  BRE_B = 'bre_b',
}

export enum MerchantPayoutFlow {
  SETTLEMENT_WALLET = 'settlement_wallet',
  DIRECT = 'direct',
}

/** POST `v2/merchants` */
export interface CreateMerchantPayload {
  name: string;
  ownerEmail: string;
  payoutFlow: MerchantPayoutFlow;
  ownerFullName?: string;
  groupAccountId?: string;
  shift4MerchantId?: string;
  mainFiatCurrency?: CurrencyCode;
}

export const MerchantBusinessCategories = {
  CLIENT: 'client',
  PARENT_COMPANY: 'parent_company',
  SUBSIDIARY: 'subsidiary',
  SUPPLIER: 'supplier',
};

export enum DepositAccountType {
  CHECKING = 'checking',
  SAVINGS = 'savings',
}

export enum PwcThemeMode {
  Light = 'light',
  Dark = 'dark',
  SameAsBrowser = 'sameAsBrowser',
}

export interface ProviderTOS {
  uuid: string;
  merchantUuid: string;
  provider: LiquidityProvider;
  acceptedAt: string;
}

export interface MerchantInfo extends MerchantCheckoutSettings {
  uuid: string;
  name: string;
  createdAt: string;
  bridgeCustomerId: string;
  mainBankAccountUuid: string | null;
  status: MerchantStatus;
  shift4MerchantId: string | null;
  ownerEmail: string;
  ownerFullName: string;
  processingFee: number;
  webhookUrl: string | null;
  meshSubclientId: string | null;
  payout: {
    nextPayoutAmount: string;
    nextPayout: string;
    paymentRailConfig?: {
      minAmount: number;
      fixedFee: number;
    };
  };
  groupAccount?: {
    uuid: string;
    name: string;
  };
  kyc?: MerchantKyc;
  refundWalletBalance: string;
  paymentRail?: BridgeFiatRail;
  payoutFlow: MerchantPayoutFlow;
  payoutSchedule: PayoutSchedule | null;
  payoutMemo?: string | null;
  acceptedTOS: ProviderTOS[];
  onboardingToken: string | null;
  mainFiatCurrency: CurrencyCode;
  settlementWalletBalance: string;
  pwcEnabled: boolean;
  stablecoinSettlementEnabled: boolean;
  cryptoPayoutEnabled: boolean;
  cryptoPayoutBalances: {
    total: string;
    inProgress: string;
    available: string;
  };
}

export interface MerchantKyc {
  uuid?: string;
  merchantUuid?: string;
  tosLink?: string;
  kycStatus?: MerchantKycStatuses;
  tosStatus?: string;
  rejectionReasons?: any[];
  updatedAt?: Date;
  autoKycErrors?: ValidationAPIError;
}

export enum MerchantKycStatuses {
  NotStarted = 'not_started',
  Pending = 'pending',
  Incomplete = 'incomplete',
  AwaitingUbo = 'awaiting_ubo',
  ManualReview = 'manual_review',
  UnderReview = 'under_review',
  Approved = 'approved',
  Rejected = 'rejected',
  Active = 'active',
}

export interface MerchantCheckoutSettings {
  title: string | null;
  lightModeLogoUrl: File | string;
  darkModeLogoUrl: File | string;
}

interface MerchantBankAddress {
  streetLine1: string;
  streetLine2?: string;
  city: string;
  state?: string;
  postalCode: string;
  country: string;
}

export interface MerchantBankInfo {
  uuid: string;
  merchantUuid: string;
  accountType: string;
  currency: string;
  bankName: string;
  createdAt: string;
  accountOwnerName: string;
  accountOwnerType: AccountOwnerType;
  address?: MerchantBankAddress;
  firstName?: string;
  lastName?: string;
  businessName?: string;
  account?: {
    accountNumber: string;
    routingNumber: string;
    checkingOrSavings?: DepositAccountType;
  };
  iban?: {
    accountNumber: string;
    bic: string;
    country?: string;
  };
  swift?: {
    accountNumber: string;
    bic: string;
    country?: string;
  };
  clabe?: {
    accountNumber: string;
  };
  pixKey?: {
    pixKey: string;
    documentNumber?: string;
  };
  breB?: {
    breBKey: string;
  };
  brCode?: {
    brCode: string;
    documentNumber?: string;
  };
  bridgeExternalAccountId: string;
  isMain?: boolean;
  paymentRail: BridgeFiatRail;
  category?: string;
  purposeOfFunds?: string;
}

export interface AutoCreateMerchantBankAccount {
  fulfilledCount: number;
  rejectedCount: number;
  rejectedReasons: ['string'];
  createdAccounts: MerchantBankInfo[];
}

export interface SimulateDeposit {
  uuid: string;
  name: string;
  title: string;
  shift4MerchantId: string;
  webhookUrl: string;
  processingFee: number;
  status: SimulateDepositStatus;
  lightModeLogoUrl: string;
  darkModeLogoUrl: string;
  meshSubclientId: string;
}

export enum SimulateDepositStatus {
  Inactive = 'inactive',
  Active = 'active',
  Deactivated = 'deactivated',
}

export type CreateBankAccountPayload = {
  uuid: string;
  paymentRail?: BridgeFiatRail;
  accountType?: ExternalAccountType;
  bankName?: string;
  accountOwnerName?: string;
  accountOwnerType?: AccountOwnerType;
  currency: string;
  address?: MerchantBankAddress;
  category?: string;
  purposeOfFunds?: string;
  shortBusinessDescription?: string;
  account?: {
    checkingOrSavings?: DepositAccountType;
    accountNumber: string;
    routingNumber: string;
  };
  iban?: {
    accountNumber: string;
    bic: string;
    country?: string;
  };
  pixKey?: {
    pixKey: string;
    documentNumber?: string;
  };
  breB?: {
    breBKey: string;
  };
  brCode?: {
    brCode: string;
    documentNumber?: string;
  };
  firstName?: string;
  lastName?: string;
  businessName?: string;
};

export type PayoutSchedule = {
  frequency: FiatPayoutFrequency;
  days?: Weekday[];
  dates?: DayOfMonth[];
  time: OffsetTime;
};

export type MerchantPayoutSettingsPayload = {
  merchantId: string;
};

export type FiatPayoutSettingsPayload = MerchantPayoutSettingsPayload & {
  payoutDestinationUuid?: string;
  paymentRail?: BridgeFiatRail;
  payoutMemo?: string;
  payoutSchedule?: PayoutSchedule;
};

export type CryptoPayoutSettingsPayload = MerchantPayoutSettingsPayload & {
  assetUuid: string;
  wallet: {
    address: string;
    memo?: string;
  };
};

export type UpdateMerchantKybPayload = {
  uuid: string;
  name: string;
  description: string;
  businessIndustry: string;
  email: string;
  businessType: BridgeCustomerBusinessType;
  website: string;
  registeredAddress: BridgeCustomerAddress;
  physicalAddress: BridgeCustomerAddress;
  hasMaterialIntermediaryOwnership: boolean;
  identifyingInformation: BridgeIdentityInformation[];
  documents: BridgeDocument[];
  associatedPersons: BridgeAssociatedPerson[];
  isHighRiskActivities: boolean;
  isRegulatedActivity: boolean;
  sourceOfFunds: BridgeSourceOfFunds;
  accountPurpose: BridgeAccountPurpose;

  highRiskActivities?: BridgeHighRiskActivities[];
  regulatedActivity?: BridgeRegulatedActivity;
  sourceOfFundsDescription?: string;
  estimatedAnnualRevenueUsd?: string;
  expectedMonthlyPaymentsUsd?: number;
  operatesInProhibitedCountries?: boolean;
};

export type PayoutDestinations = {
  uuid: string;
  type: PayoutDestinationType;
  currency: string;
  paymentRail: string;
  account: string;
  isMain: boolean;
  name?: string;
};

export enum PayoutDestinationType {
  Bank = 'bank_account',
  Crypto = 'crypto',
}

export interface MerchantWalletTransactionsQuery {
  filters?: {
    uuid?: string | string[];
    date?: DateFilterValue;
    status?: string;
  };
  pagination?: RequestPagination;
  sort?: RequestSorting;
}

export type MerchantMemoResponse = {
  currency: string;
  bankName: string;
  bankAddress: string;
  bankRoutingNumber: string;
  bankAccountNumber: string;
  bankBeneficiaryName: string;
  bankBeneficiaryAddress: string;
  paymentRails: BridgeFiatRail[];
  depositMessage: string;
  brCode?: string | null;
  iban?: string | null;
  clabe?: string | null;
  breBKey?: string | null;
  accountHolderName?: string | null;
};

export interface RefundReserveResponse {
  settlementBalance: string;
  reserveBalance: string;
  target: number | null;
  openRefundsTotal: string;
  availableForRefund: string;
  fiatCurrencyCode: string;
  settlementCurrencyCode?: string;
  displayFxRate?: string | null;
  topUpInstructions?: MerchantMemoResponse | null;
}

export interface CryptoPayoutRequest {
  customer: {
    hasPhysicalAddress: boolean;
    email: string;
    firstName: string;
    lastName: string;
    dob: string;
    countryOfResidence: string;
  };
  fiatAmount: number;
  fiatCurrencyCode: string;
  externalPayoutId?: string;
}
export interface AssociatedPersonAddress {
  streetLine1: string;
  streetLine2?: string;
  city: string;
  subdivision?: string;
  postalCode: string;
  country: string;
}

export interface AssociatedPersonIdentifyingInfo {
  type: string;
  issuingCountry: string;
  number: string;
  imageFront?: string;
}

export interface AssociatedPersonDocument {
  purposes: string[];
  file: string;
  description?: string;
}

export interface AssociatedPerson {
  id: string;
  kycScreen?: { screenedAt: string; result: string };
  ofacScreen?: { screenedAt: string; result: string };
  firstName: string;
  lastName: string;
  email: string;
  residentialAddress: AssociatedPersonAddress;
  birthDate: string;
  hasOwnership: boolean;
  hasControl: boolean;
  isSigner: boolean;
  identifyingInformation: AssociatedPersonIdentifyingInfo[];
  phone?: string;
  isDirector?: boolean;
  title?: string;
  ownershipPercentage?: string;
  relationshipEstablishedAt?: string;
  documents?: AssociatedPersonDocument[];
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateAssociatedPersonPayload {
  kycScreen: { screenedAt: string; result: string };
  ofacScreen: { screenedAt: string; result: string };
  firstName: string;
  lastName: string;
  email: string;
  residentialAddress: AssociatedPersonAddress;
  birthDate: string;
  hasOwnership: boolean;
  hasControl: boolean;
  isSigner: boolean;
  identifyingInformation: AssociatedPersonIdentifyingInfo[];
  phone?: string;
  isDirector?: boolean;
  title?: string;
  ownershipPercentage?: string;
  relationshipEstablishedAt?: string;
  documents?: AssociatedPersonDocument[];
}

export interface UpdateAssociatedPersonPayload {
  kycScreen?: { screenedAt: string; result: string };
  ofacScreen?: { screenedAt: string; result: string };
  firstName?: string;
  lastName?: string;
  email?: string;
  residentialAddress?: AssociatedPersonAddress;
  birthDate?: string;
  hasOwnership?: boolean;
  hasControl?: boolean;
  isSigner?: boolean;
  identifyingInformation?: AssociatedPersonIdentifyingInfo[];
  phone?: string;
  isDirector?: boolean;
  title?: string;
  ownershipPercentage?: string;
  relationshipEstablishedAt?: string;
  documents?: AssociatedPersonDocument[];
}
export interface BridgeKycAssociatedPersonRequirement {
  associatedPerson: string;
  items: string[];
}

export interface BridgeKycRequirements {
  complete: Array<string | BridgeKycAssociatedPersonRequirement>;
  pending: Array<string | BridgeKycAssociatedPersonRequirement>;
  missing: Array<string | BridgeKycAssociatedPersonRequirement>;
  issues: Array<string | BridgeKycAssociatedPersonRequirement>;
}

export interface BridgeKycInfoData {
  name: string;
  status: string;
  requirements: BridgeKycRequirements | null;
  additional_requirements: string[];
}

export type BridgeKycInfoResponse = Response<BridgeKycInfoData | null>;

export type TitanKybDataResponse = Response<{
  merchant: FormType;
  bankAccounts: MerchantBankInfo[];
  associatedPersons: AssociatedPersonType[];
}>;
export type MerchantsPayoutDestinationsResponse = { data: PayoutDestinations[] };
export type MerchantWalletTransactionsResponse = { data: CustodialWalletTransaction[]; pagination: ServerPagination };
export type CryptoPayoutResponse = Response<CryptoPayout>;
export type AssociatedPersonsListResponse = Response<{ data: AssociatedPerson[] }>;
export type AssociatedPersonResponse = Response<AssociatedPerson>;
export type CreateAssociatedPersonResponse = Response<AssociatedPerson>;
export type UpdateAssociatedPersonResponse = Response<AssociatedPerson>;
export type MerchantsListResponse = { data: MerchantInfo[]; pagination: ServerPagination };
export type MerchantsInfoResponse = { data: MerchantInfo };
export type CreateMerchantBankAccountResponse = Response<MerchantBankInfo>;
export type AutoCreateMerchantBankAccountResponse = Response<AutoCreateMerchantBankAccount>;
export type SimulateDepositResponse = Response<{ data: SimulateDeposit }>;
